import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { AppTextField } from 'src/components/AppTextField'
import { ColorPicker, getNoteColorKey } from 'src/components/ColorPicker'
import { filterProductOptions, formatProductLabel, renderProductOption } from 'src/components/common/ProductOption'
import { FlexSpinner } from 'src/components/Spinner'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { z } from 'zod'
import { useGetComponents } from '../../../api/components'
import { useGetProductsBySupplierId } from '../../../api/product'
import { useGetProductCertificationByProductId } from '../../../api/product-certification'
import { useGetAdminSuppliersHasItems } from '../../../api/supplier'
import { useGetUnits } from '../../../api/unit'
import BasicDatePicker from '../../../components/DateTimePicker'
import { AppForm, AppFormProps, FormFileCoreUpload, FormTextField } from '../../../components/FormCore'
import { GenericDialogLayout } from '../../../components/GenericDialogLayout'
import { MainButton } from '../../../components/MainButton'
import { APIResultCount } from '../../../constant'
import { ComponentPlacementType, FileType, NoteColor } from '../../../enums'
import { IProduct, ISupplier, IUnit } from '../../../generated-types'
import { useZodForm } from '../../../hooks/zod-form'
import { CustomAutoComplete } from './CustomAutocomplete'
import { apiConsts } from 'src/api/apiConsts'
import { useGetProjectComponents } from 'src/api/projectComponent'

interface ComponentFormProps extends AppFormProps {
  customerId: EntityId
  positionId: EntityId
  title: string
  isLoading: boolean
  componentPlacement?: ComponentPlacementType
  isMultiPlacementsEnabled?: boolean
  isProjectComponentForm?: boolean
  projectId?: EntityId
  isDrawingNumberEmpty?: boolean
}

export const ComponentForm: React.FC<ComponentFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  customerId,
  positionId,
  title,
  isLoading,
  componentPlacement,
  isMultiPlacementsEnabled,
  isProjectComponentForm = false,
  projectId,
  isDrawingNumberEmpty,
}) => {
  const { t: translate } = useTranslation(['component', 'common'])
  const theme = useTheme()
  const schema = z.object({
    productId: z.union([z.number(), z.string().nonempty(translate('productIsRequired'))]),
    productNumber: z.string().nonempty(translate('productNumberIsRequired')),
    productDescription: z.string().nonempty(translate('productDescriptionIsRequired')),
    supplierId: z.union([z.number(), z.string().nonempty(translate('supplierIsRequired'))]),
    quantity: z.union([
      z
        .number()
        .min(1, { message: translate('quantityIsRequired') })
        .positive(translate('quantityCantBeNegative')),
      z
        .string()
        .min(1, { message: translate('quantityIsRequired') })
        .refine(v => !isNaN(Number(v)) && Number(v) > 0, { message: translate('quantityCantBeNegative') }),
    ]),
    positionDrawingNumber: z
      .union([
        z.number().min(1, translate('drawingInNumberIsRequired')).positive(translate('drawingInNumberCantBeNegative')),
        z
          .string()
          .min(1, translate('drawingInNumberIsRequired'))
          .refine(v => !isNaN(Number(v)) && Number(v) > 0, { message: translate('drawingInNumberCantBeNegative') }),
      ])
      .refine(
        value => {
          let componentsToCheck = components?.items || projectComponents?.items || []

          if (isMultiPlacementsEnabled && componentPlacement !== undefined) {
            componentsToCheck = componentsToCheck.filter(component => component.placement === componentPlacement)
          }

          const duplicateExists = componentsToCheck.some(component => {
            const isSameDrawingNumber = component.positionDrawingNumber.toString() === value.toString()
            const isDifferentComponent = isUpdateForm ? component.id !== initialValues.id : true
            return isSameDrawingNumber && isDifferentComponent
          })

          return !duplicateExists
        },
        {
          message: translate('duplicateDrawingNumber', { ns: 'component' }),
        },
      ),
    unitId: z.union([z.number(), z.string().nonempty(translate('unitIsRequired'))]),
    notes: z.string(),
    installationDate: z.date().optional().nullable().or(z.string().optional()).nullable(),
    imageUrl: z.string().optional().or(z.null()),
    overriddenLifetime: z.string(),
    minimumBreakingLoad: z.string(),
    potencialCertificateBNs: z.string().optional(),
    fileId: z.union([z.string(), z.number()]).optional().or(z.null()),
    placement: z.number().default(ComponentPlacementType.Lifted),
    overridenUnitPrice: z
      .union([z.string().transform(val => (val === '' ? null : parseFloat(val))), z.number(), z.null()])
      .refine(val => val === null || val === undefined || !isNaN(Number(val)), {
        message: translate('unit-price-required', { ns: 'component' }),
      })
      .optional()
      .nullable(),
    noteColor: z.nativeEnum(NoteColor).optional().nullable(),
    isReused: z.boolean().optional().nullable(),
  })

  const parsedInitialValues = initialValues
    ? {
        ...initialValues,
        quantity: initialValues.quantity ? String(initialValues.quantity) : '',
        positionDrawingNumber: initialValues.positionDrawingNumber ? String(initialValues.positionDrawingNumber) : '',
        overriddenLifetime: initialValues.overriddenLifetime ? String(initialValues.overriddenLifetime) : '',
        minimumBreakingLoad:
          initialValues.minimumBreakingLoad !== null ? String(initialValues.minimumBreakingLoad) : '',
        potencialCertificateBNs: initialValues.potencialCertificateBNs
          ? String(initialValues.potencialCertificateBN)
          : '',
        imageUrl: initialValues.imageUrl ? String(initialValues.imageUrl) : '',
        notes: initialValues.notes ? String(initialValues.notes) : '',
        noteColor: getNoteColorKey(initialValues.noteColor),
      }
    : undefined

  const form = useZodForm(schema, { defaultValues: parsedInitialValues })
  const { setValue, control } = useZodForm(schema)
  const isUpdateForm = parsedInitialValues && parsedInitialValues.id
  const [supplier, setSupplier] = useState<ISupplier | null>(null)
  const [product, setProduct] = useState<IProduct | null>(null)
  const [batchNumbersWithCertificates, setBatchNumbersWithCertificates] = useState<string[]>([])
  const [defaultBatchNumbers, setDefaultBatchNumbers] = useState<string[]>([])
  const [unitId, setUnitId] = useState<number>(parsedInitialValues?.unitId)
  const [inputBatchNumber, setInputBatchNumber] = useState<string>('')

  const { data: products, isLoading: isProductLoading } = useGetProductsBySupplierId(
    supplier?.id ? supplier.id : 0,
    0,
    APIResultCount.Max,
  )
  const { data: suppliers, isLoading: isSupplierLoading } = useGetAdminSuppliersHasItems(0, APIResultCount.Max)
  const { data: units, isLoading: isUnitsLoading } = useGetUnits(apiConsts.Page, apiConsts.Max)
  const { data: certifications, isLoading: isCertificationsLoading } = useGetProductCertificationByProductId(
    product?.id as EntityId,
  )
  const { data: components, isLoading: isComponentLoading } = useGetComponents(
    customerId as EntityId,
    positionId as EntityId,
    apiConsts.Page,
    apiConsts.Max,
    !isProjectComponentForm,
  )

  const { data: projectComponents, isLoading: isProjectComponentLoading } = useGetProjectComponents(
    customerId as EntityId,
    positionId as EntityId,
    apiConsts.Page,
    apiConsts.Max,
    !!projectId,
  )

  useEffect(() => {
    const batchNumbers = initialValues?.potencialCertificateBNs ? String(initialValues.potencialCertificateBNs) : ''
    if (batchNumbers && batchNumbers.length > 0) {
      setDefaultBatchNumbers(batchNumbers.split(',').map(option => option))
    } else {
      setDefaultBatchNumbers([])
    }
  }, [initialValues])

  useEffect(() => {
    if (suppliers?.items?.length) {
      setSupplier((suppliers?.items.find(s => s.id === parsedInitialValues?.supplierId) as ISupplier) || null)
    }
  }, [suppliers])

  useEffect(() => {
    setProduct((products?.items.find(p => p.id === parsedInitialValues?.productId) as IProduct) || null)
  }, [products])

  useEffect(() => {
    const arr =
      certifications?.flatMap(
        c =>
          c.batchNumbers
            ?.split(',')
            .map(s => s.trim())
            .filter(Boolean) ?? [],
      ) ?? []
    setBatchNumbersWithCertificates(Array.from(new Set(arr)))
  }, [product?.id, certifications])

  useEffect(() => {
    if (product) {
      form.setValue('productNumber', product?.number)
      form.setValue('productDescription', product?.description)
      form.setValue('unitId', product?.unitId)
      isUpdateForm && initialValues?.productId === product?.id
        ? form.setValue(
            'minimumBreakingLoad',
            initialValues?.minimumBreakingLoad ? String(initialValues?.minimumBreakingLoad) : '',
          )
        : form.setValue('minimumBreakingLoad', product?.minimumBreakingLoad ? String(product.minimumBreakingLoad) : '')
      setValue('productDescription', product?.description)
      setValue('productNumber', product?.number, { shouldValidate: true })
      setValue('unitId', product?.unitId, { shouldValidate: true })
      if (initialValues?.productId !== product?.id) {
        form.setValue('overridenUnitPrice', null, { shouldValidate: true })
        form.setValue('overriddenLifetime', '', { shouldValidate: true })
        form.setValue('notes', '', { shouldValidate: true })
        form.setValue('noteColor', null, { shouldValidate: true })
        form.setValue('isReused', false, { shouldValidate: true })
      }
      setUnitId(product?.unitId)
    }
  }, [product, isUpdateForm, initialValues, form, setValue])

  useEffect(() => {
    if (isDrawingNumberEmpty) {
      form.setValue('positionDrawingNumber', '')
      return
    }
    if (
      !isUpdateForm &&
      ((components && components?.items?.length > 0) || (projectComponents && projectComponents?.items?.length > 0))
    ) {
      let relevantComponents = projectId ? [...projectComponents!.items] : [...components!.items]
      if (isMultiPlacementsEnabled && componentPlacement !== undefined) {
        relevantComponents = relevantComponents.filter(component => component.placement === componentPlacement)
      }

      if (relevantComponents.length > 0) {
        const componentsSorted = relevantComponents?.sort((a, b) => a.positionDrawingNumber - b.positionDrawingNumber)

        for (let i = 0; i < componentsSorted.length; i++) {
          if (componentsSorted[i]?.positionDrawingNumber + 1 !== componentsSorted[i + 1]?.positionDrawingNumber) {
            form.setValue('positionDrawingNumber', (componentsSorted[i]?.positionDrawingNumber + 1).toString())
            break
          } else if (componentsSorted[i]?.positionDrawingNumber !== 1 && i == 0) {
            form.setValue('positionDrawingNumber', (i + 1).toString())
            break
          }
        }
      } else {
        form.setValue('positionDrawingNumber', '1')
      }
    } else if (!isUpdateForm) {
      form.setValue('positionDrawingNumber', '1')
    }
  }, [isUpdateForm, components, projectComponents, componentPlacement, form, isMultiPlacementsEnabled])

  useEffect(() => {
    form.setValue('placement', componentPlacement ?? ComponentPlacementType.Lifted)
  }, [componentPlacement])

  const handleSelectIDChange = (value, name, onAction) => {
    const id = value
    form.setValue(name, id)
    form.clearErrors(name)
    onAction(id)
  }

  const handleSubmit = useCallback(
    f => {
      f.supplierId = supplier?.id
      f.productId = product?.id
      f.unitId = unitId
      f.id = isUpdateForm

      onSubmit(f)
    },

    [supplier, product, unitId, defaultBatchNumbers, components],
  )

  const handleComponentSubmit = () => {
    if (inputBatchNumber && defaultBatchNumbers.includes(inputBatchNumber.trim())) {
      toast.warn(`"${inputBatchNumber}" ${translate('already-exists', { ns: 'position' })}`)
    }

    let updatedBatchNumbers = [...defaultBatchNumbers]

    if (inputBatchNumber && !defaultBatchNumbers.includes(inputBatchNumber.trim())) {
      updatedBatchNumbers = [...defaultBatchNumbers, inputBatchNumber.trim()]
      setDefaultBatchNumbers(updatedBatchNumbers)
    }

    form.setValue('potencialCertificateBNs', updatedBatchNumbers.join(','))
    setInputBatchNumber('')
    form.handleSubmit(handleSubmit)()
  }

  return (
    <GenericDialogLayout
      title={title}
      pop={onCancel}
      removedCloseButton={false}
      actions={[
        <MainButton
          key="submit-btn"
          onClick={() => {
            handleComponentSubmit()
          }}
          loading={isLoading}
        >
          {isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      {isLoading ||
      isUnitsLoading ||
      isProductLoading ||
      isSupplierLoading ||
      isComponentLoading ||
      isProjectComponentLoading ||
      isCertificationsLoading ? (
        <FlexSpinner />
      ) : (
        <AppForm form={form} onSubmit={handleSubmit} hasInitialValues={parsedInitialValues}>
          {isUpdateForm && <FormTextField name="id" type="hidden" />}
          <Grid container>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id="supplierId"
                options={suppliers?.items as ISupplier[]}
                onChange={(_, value) => {
                  setSupplier(suppliers?.items.find(s => s.id === value?.id) as ISupplier)

                  form.setValue('supplierId', value?.id as string | number)
                  form.clearErrors('supplierId')
                }}
                getOptionLabel={option => option.name ?? (option.name || null)}
                value={supplier}
                renderOption={(props, o) => (
                  <MenuItem {...props} key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                )}
                renderInput={params => (
                  <AppTextField
                    {...params}
                    name="supplierId"
                    label={translate('supplier', { ns: 'common' })}
                    error={!!form.formState.errors.supplierId}
                  />
                )}
              />
              {form.formState.errors.supplierId && (
                <FormHelperText sx={{ py: 1, px: 1 }}>
                  {translate('supplierIs', { ns: 'component' })}
                  {form.formState.errors.supplierId.message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                id="productId"
                options={(products?.items as IProduct[]) ?? []}
                onChange={(_, value) => {
                  setProduct(products?.items.find(s => s.id === value?.id) as IProduct)

                  form.setValue('productId', value?.id as string | number)
                  form.clearErrors('productId')
                }}
                value={product}
                getOptionLabel={formatProductLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={renderProductOption}
                filterOptions={filterProductOptions}
                renderInput={params => (
                  <AppTextField
                    {...params}
                    name="productId"
                    label={translate('product', { ns: 'common' })}
                    error={!!form.formState.errors.productId}
                  />
                )}
              />
              {form.formState.errors.productId && (
                <FormHelperText sx={{ py: 1, px: 1 }}>
                  {translate('productIs', { ns: 'component' })}
                  {form.formState.errors.productId.message}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
          {product?.id && (
            <>
              <Grid container>
                <Grid item xs={6} sm={4}>
                  <FormTextField
                    name="productNumber"
                    label={translate('productNumber', { ns: 'component' })}
                    disabled={isUpdateForm || isProjectComponentForm}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormTextField
                    name="productDescription"
                    label={translate('productDescription', { ns: 'component' })}
                    disabled={isProjectComponentForm}
                  />
                </Grid>
                <Grid item xs={6} sm={4} px={1} py={1}>
                  <FormControl variant="filled" fullWidth error={!!form.formState.errors.unitId}>
                    <InputLabel id="unitId">{translate('unit', { ns: 'component' })}</InputLabel>
                    <Controller
                      name="unitId"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, value } }) => {
                        if (unitId) {
                          value = unitId
                        }
                        return (
                          <Select
                            fullWidth
                            name="unitId"
                            value={value}
                            onChange={event => {
                              onChange(event)
                              handleSelectIDChange(event.target.value, 'unitId', setUnitId)
                            }}
                            defaultValue={unitId}
                            displayEmpty
                            label={translate('unit', { ns: 'component' })}
                            variant="filled"
                          >
                            {units?.items.map((unit: IUnit) => (
                              <MenuItem key={unit.id} value={unit.id}>
                                {unit.name} ({unit.abbreviation})
                              </MenuItem>
                            ))}
                          </Select>
                        )
                      }}
                    />
                    {form.formState.errors.unitId && (
                      <FormHelperText sx={{ py: 1, px: 1 }}>
                        {translate('unitIs', { ns: 'component' })}
                        {form.formState.errors.unitId.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6} sm={4}>
                  <CustomAutoComplete
                    inputValue={inputBatchNumber}
                    setInputValue={setInputBatchNumber}
                    batchNumbersWithCertificates={batchNumbersWithCertificates}
                    batchNumbers={defaultBatchNumbers}
                    setBatchNumbers={setDefaultBatchNumbers}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormTextField
                    inputProps={{ type: 'number' }}
                    name="quantity"
                    label={translate('quantity', { ns: 'component' })}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormTextField
                    inputProps={{ type: 'number' }}
                    name="positionDrawingNumber"
                    label={translate('drawingInNumber', { ns: 'component' })}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6} sm={4}>
                  <FormTextField
                    inputProps={{ type: 'number' }}
                    name="overriddenLifetime"
                    label={translate('overriddenLifetime', { ns: 'component' })}
                  />
                  {product?.expectedLifetime ? (
                    <FormHelperText sx={{ py: 1, px: 1 }} id="component-helper-text">
                      {translate('expectedLifetime', { ns: 'component' })}: {product?.expectedLifetime}
                    </FormHelperText>
                  ) : (
                    ''
                  )}
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormTextField
                    inputProps={{ type: 'number' }}
                    name="minimumBreakingLoad"
                    label={translate('minimumBreakingLoad', { ns: 'component' })}
                    disabled
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  {isProjectComponentForm ? (
                    <FormTextField type="number" name="overridenUnitPrice" label={translate('unit-price')} />
                  ) : (
                    <BasicDatePicker
                      name="installationDate"
                      label={translate('installationDate', { ns: 'component' })}
                      allowNullable
                    />
                  )}
                </Grid>
              </Grid>
              {isMultiPlacementsEnabled && (
                <Grid item xs={8} sm={6}>
                  <Box display={'flex'} flexDirection={'row'} p={1} gap={1} alignItems={'center'}>
                    <Typography>{translate('placementBadge', { ns: 'component' })}</Typography>
                    <Chip
                      label={ComponentPlacementType[componentPlacement ?? 0]}
                      sx={{
                        backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
                        color: getThemeColor(theme, MUIThemeColors.white),
                      }}
                    />
                  </Box>
                </Grid>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={isProjectComponentForm && form.watch('notes') ? 7 : 12}>
                  <FormTextField
                    name="notes"
                    label={translate('notes', { ns: 'component' })}
                    multiline
                    rows={4}
                    fullWidth
                  />
                </Grid>
                {isProjectComponentForm && form.watch('notes') && (
                  <Grid item xs={12} md={5}>
                    <ColorPicker
                      name="noteColor"
                      label={translate('color', { ns: 'component' }) || 'Color'}
                      setValue={(name, value) => form.setValue(name as any, value)}
                      value={form.watch('noteColor')}
                      error={form.formState.errors.noteColor?.message}
                    />
                  </Grid>
                )}
              </Grid>
              <Box py={1}>
                <FormFileCoreUpload
                  label={translate('image', { ns: 'component' })}
                  name="imageUrl"
                  mapFileKey="url"
                  type={FileType.Image}
                  initialFiles={initialValues?.file}
                  height="150px"
                  getUploadedFiles={e => getUploadedFile(e, form)}
                />
              </Box>
            </>
          )}
        </AppForm>
      )}
    </GenericDialogLayout>
  )
}

const getUploadedFile = (e, form) => {
  form.setValue('fileId', e.id)
}
